/**
 * AuditReviewV2 — CP2–CP10 comprehensive audit walkthrough for Bryan.
 *
 * Covers EVERY existing question plus every proposed new question.
 * Bryan can approve/reject/edit-text/skip each card.
 * He can also add his own proposed questions per gate using the
 * "+ Add my own question to CP{n}" button.
 *
 * localStorage key: "aip:cp210-audit-v2:v1"
 * Export filename: cp2-10-audit-v2-decisions-[date].json
 *
 * DO NOT touch /audit (v1) — it remains live with Bryan's first-round responses.
 * DO NOT touch CP1 — it is locked.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "wouter";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CardType = "EXISTING" | "NEW";
type Recommendation = "KEEP" | "EDIT" | "REMOVE" | "ADD" | "MOVE-IN";
type AuditDecision = "keep" | "replace" | "edit" | "skip";

interface AuditCard {
  id: string;
  cp: number;
  type: CardType;
  questionKey: string | null; // null for truly new questions
  label: string;             // question text shown to Bryan
  optionsSummary: string;    // comma-list of option labels
  recommendation: Recommendation;
  rationale: string;
  hasHardStop: boolean;
}

interface CardDecision {
  decision: AuditDecision;
  editedText?: string;       // populated when decision === "edit"
  notes?: string;
  decidedAt: string;
}

interface UserAddedQuestion {
  id: string;
  prompt: string;
  rationale: string;
  options: string; // free text, one option per line
  createdAt: string;
}

interface AuditStateV2 {
  decisions: Record<string, CardDecision>;
  userAdded: Record<string, UserAddedQuestion[]>; // keyed by cp number string "2", "3", …
  startedAt: string;
}

// ---------------------------------------------------------------------------
// Gate metadata
// ---------------------------------------------------------------------------

// Canonical CP gate names — locked 2026-05-26 per helix-checkpoints.ts
const GATE_NAMES: Record<number, string> = {
  1:  "Project",
  2:  "Need / Pain / Demonstration",
  3:  "Budget",
  4:  "Timeline",
  5:  "Authority — Project / Initiative",
  6:  "Technical and Financial Sign-off",
  7:  "Org Structure and Influence Map",
  8:  "Competitive Position",
  9:  "Compliance — IT Internal and Industry-Specific",
  10: "Legal — Contracts, SLAs, Terms",
};

const GATES = [2, 3, 4, 5, 6, 7, 8, 9, 10];

// ---------------------------------------------------------------------------
// Recommendation badge styles
// ---------------------------------------------------------------------------

const REC_BADGE: Record<Recommendation, { bg: string; color: string }> = {
  KEEP:     { bg: "#D1FAE5", color: "#065F46" },
  EDIT:     { bg: "#FEF3C7", color: "#92400E" },
  REMOVE:   { bg: "#FEE2E2", color: "#991B1B" },
  ADD:      { bg: "#EFEFEE", color: "#0B0B0F" },
  "MOVE-IN":{ bg: "#FEE2E2", color: "#0B0B0F" },
};

// Decision badge
const DECISION_BADGE: Record<AuditDecision, { bg: string; color: string; label: string }> = {
  keep:    { bg: "#D1FAE5", color: "#065F46", label: "Kept" },
  replace: { bg: "#FEF3C7", color: "#92400E", label: "Replace" },
  edit:    { bg: "#EFEFEE", color: "#0B0B0F", label: "Edit text" },
  skip:    { bg: "#F3F4F6", color: "#6B7280", label: "Skipped" },
};

function decisionBorderColor(d: AuditDecision | undefined): string {
  if (d === "keep")    return "#16A34A";
  if (d === "replace") return "#EAB308";
  if (d === "edit")    return "#525258";
  if (d === "skip")    return "#D1D5DB";
  return "#E5E7EB";
}

// ---------------------------------------------------------------------------
// AUDIT CARDS — Every CP2–CP10 question + all proposed additions
// Generated from questions.ts + cp2-10-audit-v2-recommendations.md
// ---------------------------------------------------------------------------

const ALL_CARDS: AuditCard[] = [
  // =========================================================================
  // CP 2 — Need
  // =========================================================================
  {
    id: "cp2_e01",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_finance_coverage",
    label: "Finance buyer covered? (CFO / VP Fin / Comptroller)",
    optionsSummary: "Confirmed multi-thread / Single contact / Unknown",
    recommendation: "KEEP",
    rationale: "Finance buyer coverage is a core qualify signal — no Finance contact, no commercial path.",
    hasHardStop: false,
  },
  {
    id: "cp2_e02",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_ops_coverage",
    label: "Operations buyer covered? (President / COO / VP Ops)",
    optionsSummary: "Confirmed multi-thread / Single contact / Unknown",
    recommendation: "KEEP",
    rationale: "Ops coverage is required for manufacturing implementation sign-off.",
    hasHardStop: false,
  },
  {
    id: "cp2_e03",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_hr_coverage",
    label: "Have you confirmed at least one HR/People Ops contact in the buying coalition?",
    optionsSummary: "Confirmed / Single contact / Unknown",
    recommendation: "REMOVE",
    rationale: "Manufacturing deals do not pivot on HR coverage. This is gate-padding.",
    hasHardStop: false,
  },
  {
    id: "cp2_e04",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_champion",
    label: "Champion identified? (Named, coached, repeats our message)",
    optionsSummary: "Named + coached / Named only / No champion (Review)",
    recommendation: "KEEP",
    rationale: "Champion identification is critical — no champion, no internal advocacy.",
    hasHardStop: false,
  },
  {
    id: "cp2_e05",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_economic_buyer",
    label: "Economic buyer identified?",
    optionsSummary: "Named with veto power / Suspected / Unknown (Review)",
    recommendation: "KEEP",
    rationale: "EB identification is mandatory — without it we cannot forecast or build a commercial path.",
    hasHardStop: false,
  },
  {
    id: "cp2_e06",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_fox",
    label: "Fox (informal power broker) identified?",
    optionsSummary: "Named / Suspected / Unknown",
    recommendation: "EDIT",
    rationale: "Rename to 'Informal power broker identified?' — BDRs may not know the term 'fox'. Same signal, plain English.",
    hasHardStop: false,
  },
  {
    id: "cp2_e07",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_detractors",
    label: "Detractors mapped?",
    optionsSummary: "Named with mitigation plan / Named no plan / Unknown",
    recommendation: "KEEP",
    rationale: "Un-mapped detractors kill deals in committee. This question forces the AE to surface them.",
    hasHardStop: false,
  },
  {
    id: "cp2_e08",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_multi_thread",
    label: "Multi-thread coverage?",
    optionsSummary: "3+ stakeholders engaged / 1–2 / Single thread",
    recommendation: "KEEP",
    rationale: "Single-thread deals are the most fragile. Multi-thread is a disqualify signal if absent.",
    hasHardStop: false,
  },
  {
    id: "cp2_e09",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_exec_sponsor_level",
    label: "Executive sponsor level?",
    optionsSummary: "C-suite / VP / Director or below (Review)",
    recommendation: "KEEP",
    rationale: "Director-level sponsorship in a manufacturing enterprise deal is a soft disqualifier — decisions need VP+ authority.",
    hasHardStop: false,
  },
  {
    id: "cp2_e10",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_champion_internal_risk_taken",
    label: "Has the champion advocated without us in the room?",
    optionsSummary: "Yes — positive outcome / Yes — mixed outcome / No — has not advocated (Review)",
    recommendation: "KEEP",
    rationale: "The critical test of a real champion vs. a passive supporter. Has political capital been placed on the line?",
    hasHardStop: false,
  },
  {
    id: "cp2_e11",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_economic_buyer_direct_contact",
    label: "Economic buyer spoken to us directly?",
    optionsSummary: "Yes — direct conversation / Intro made, no substance / No direct contact (Review)",
    recommendation: "KEEP",
    rationale: "Separates 'EB named' from 'EB engaged.' Champion-reported EB preference is assumption, not fact.",
    hasHardStop: false,
  },
  {
    id: "cp2_e12",
    cp: 2,
    type: "EXISTING",
    questionKey: "cp2_competitive_ownership_conflict",
    label: "Competitive ownership conflict?",
    optionsSummary: "Clean / Unverified / Adjacent relationship / Competitor subsidiary (Disqualify) / Acquisition in-flight (Disqualify)",
    recommendation: "KEEP",
    rationale: "Magna-style ownership conflict is a hard kill — walk away. This question surfaces it.",
    hasHardStop: true,
  },
  // CP2 new
  {
    id: "cp2_n01",
    cp: 2,
    type: "NEW",
    questionKey: null,
    label: "Buying coalition assembled — are Finance, Ops, and Champion engaged and aware of each other?",
    optionsSummary: "Full coalition assembled and aligned / Finance + Ops separate, not yet convened / Champion only (Review)",
    recommendation: "ADD",
    rationale: "We test each role individually but never roll them up. A deal with three siloed contacts who have never been in a room together is not a coalition.",
    hasHardStop: false,
  },
  {
    id: "cp2_n02",
    cp: 2,
    type: "NEW",
    questionKey: null,
    label: "Have we engaged the plant/site general manager (if applicable)?",
    optionsSummary: "Engaged and supportive / Introduced not yet substantive / Not applicable / Not identified (Orange)",
    recommendation: "ADD",
    rationale: "The plant GM controls floor access, union relationships, and daily implementation. Without buy-in, deployment stalls post-signature — quietly.",
    hasHardStop: false,
  },

  // =========================================================================
  // CP 3 — Pain
  // =========================================================================
  {
    id: "cp3_e01",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_ci_culture",
    label: "Culture of continuous improvement?",
    optionsSummary: "Programmatic / Aspirational / Absent",
    recommendation: "EDIT",
    rationale: "Reframe to 'Does the buyer have an operating cadence we can plug into?' — less jargon, same signal.",
    hasHardStop: false,
  },
  {
    id: "cp3_e02",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_implementation_followthrough",
    label: "Implementation follow-through problem stated?",
    optionsSummary: "Yes — specific examples / Yes — general 'things slip' / Not stated",
    recommendation: "KEEP",
    rationale: "Follow-through gap is the most common pain we solve. If the buyer cannot name it, pain may be superficial.",
    hasHardStop: false,
  },
  {
    id: "cp3_e03",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_accountability_gap",
    label: "Enterprise accountability gap identified?",
    optionsSummary: "Named gap / Vague references / Not identified",
    recommendation: "KEEP",
    rationale: "Named accountability gap is a qualification signal that directly maps to our solution.",
    hasHardStop: false,
  },
  {
    id: "cp3_e04",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_scorecard_gap",
    label: "Scorecard / dashboard gap? (Excel / manual today?)",
    optionsSummary: "Excel / manual (best opportunity) / Partial dashboards / Mature dashboards already",
    recommendation: "KEEP",
    rationale: "The single strongest CP3 qualifier. Excel-only buyer is ready for transformation.",
    hasHardStop: false,
  },
  {
    id: "cp3_e05",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_alignment",
    label: "Alignment on goals and priorities?",
    optionsSummary: "Misaligned + acknowledged / Partial / Already aligned",
    recommendation: "REMOVE",
    rationale: "Overlaps with cp3_accountability_gap and cp3_implementation_followthrough. Three questions probing 'are people on the same page' — pick one.",
    hasHardStop: false,
  },
  {
    id: "cp3_e06",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_realtime_mobile",
    label: "Real-time data + mobile access need?",
    optionsSummary: "Yes — explicitly named / Mentioned in passing / Not raised",
    recommendation: "REMOVE",
    rationale: "Feature-shopping question, not qualify/disqualify. Does not drive the keep-working-or-stop decision.",
    hasHardStop: false,
  },
  {
    id: "cp3_e07",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_pain_owner_named",
    label: "Is there a named internal owner of this problem?",
    optionsSummary: "Named — formal performance metric / Named — informal / No named owner",
    recommendation: "KEEP",
    rationale: "Pain without an owner is a complaint, not a business problem.",
    hasHardStop: false,
  },
  {
    id: "cp3_e08",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_twelve_month_consequence",
    label: "Consequence if this problem is unchanged in 12 months?",
    optionsSummary: "Named with metric / Named directional / No consequence named (Review)",
    recommendation: "KEEP",
    rationale: "Forces the buyer to articulate a future-state cost of inaction anchored to a time horizon.",
    hasHardStop: false,
  },
  {
    id: "cp3_e09",
    cp: 3,
    type: "EXISTING",
    questionKey: "cp3_prior_attempt_to_solve",
    label: "Has this org tried to solve this problem before?",
    optionsSummary: "Yes — failed with diagnosis / Yes — failed no diagnosis / No — first attempt / Unknown",
    recommendation: "KEEP",
    rationale: "Strongest single predictor of adoption and change management risk.",
    hasHardStop: false,
  },
  // CP3 new
  {
    id: "cp3_n01",
    cp: 3,
    type: "NEW",
    questionKey: null,
    label: "Who stated this pain — economic buyer directly, champion, or AE inference?",
    optionsSummary: "EB stated with consequence / Champion reported — EB not heard / AE inferred — buyer never named it (Review)",
    recommendation: "ADD",
    rationale: "Pain sourced from the EB has fundamentally different weight than champion-reported or AE-inferred pain. If the AE cannot answer this, pain may be assumption.",
    hasHardStop: false,
  },
  {
    id: "cp3_n02",
    cp: 3,
    type: "NEW",
    questionKey: null,
    label: "Has a second, independent stakeholder confirmed the same pain?",
    optionsSummary: "Two+ people named same problem independently / Second stakeholder expressed interest / Only one person — no corroboration / Only spoken to one stakeholder (Review)",
    recommendation: "ADD",
    rationale: "One person's pain is a personal opinion. Two independent people naming the same problem is organizational signal.",
    hasHardStop: false,
  },
  {
    id: "cp3_n03",
    cp: 3,
    type: "NEW",
    questionKey: null,
    label: "Quantified deal size — have we sized the total prize in dollars?",
    optionsSummary: "Yes — dollar value documented / Partially — directional only / No — qualitative only / Buyer refuses to quantify (Review)",
    recommendation: "ADD",
    rationale: "CP1 asks 'is pain quantified at all.' This asks the AE-level version: have we sized the prize so we can justify the ROI case at CP7?",
    hasHardStop: false,
  },

  // =========================================================================
  // CP 4 — Budget
  // =========================================================================
  {
    id: "cp4_e01",
    cp: 4,
    type: "EXISTING",
    questionKey: "cp4_exec_sponsorship",
    label: "Executive sponsorship commitment?",
    optionsSummary: "Signed / written commitment / Verbal only / None",
    recommendation: "KEEP",
    rationale: "Written exec commitment separates real sponsorship from verbal encouragement.",
    hasHardStop: false,
  },
  {
    id: "cp4_e02",
    cp: 4,
    type: "EXISTING",
    questionKey: "cp4_change_mgmt",
    label: "Change management readiness?",
    optionsSummary: "Resourced — owner + budget / Planned but not resourced / Unaddressed",
    recommendation: "KEEP",
    rationale: "Unaddressed change management is the primary post-signature failure mode in manufacturing.",
    hasHardStop: false,
  },
  {
    id: "cp4_e03",
    cp: 4,
    type: "EXISTING",
    questionKey: "cp4_adoption_plan",
    label: "User adoption plan?",
    optionsSummary: "Named owner + plan / Named owner only / None",
    recommendation: "KEEP",
    rationale: "Adoption without a named plan and owner is aspiration, not commitment.",
    hasHardStop: false,
  },
  {
    id: "cp4_e04",
    cp: 4,
    type: "EXISTING",
    questionKey: "cp4_prior_initiative_failure",
    label: "Has a similar change initiative stalled or failed at this org?",
    optionsSummary: "No known failure / Prior stalled — addressed / Prior failed — unaddressed (Review) / Prior failed — unacknowledged (Review)",
    recommendation: "KEEP",
    rationale: "Directly predicts adoption risk. An org with an unacknowledged failure is the highest-risk implementation.",
    hasHardStop: false,
  },
  {
    id: "cp4_e05",
    cp: 4,
    type: "EXISTING",
    questionKey: "cp4_training_owner",
    label: "Named training and onboarding owner?",
    optionsSummary: "Named with budget + rollout plan / Named — no budget or plan / Deferred to champion informally / Not discussed",
    recommendation: "KEEP",
    rationale: "Training and adoption are different owners with different budgets. Do not merge — they are distinct conversations.",
    hasHardStop: false,
  },
  // CP4 new
  {
    id: "cp4_n01",
    cp: 4,
    type: "NEW",
    questionKey: null,
    label: "Does the buyer have the internal bench to execute this?",
    optionsSummary: "Named owners with confirmed availability / Owners named but stretched / Unknown — no resources committed / No bandwidth — buyer has named this (Review)",
    recommendation: "ADD",
    rationale: "Organizational desire and organizational capacity are different. A manufacturer at 110% capacity will want this and still stall — because no one has bandwidth to absorb it.",
    hasHardStop: false,
  },
  {
    id: "cp4_n02",
    cp: 4,
    type: "NEW",
    questionKey: null,
    label: "Internal launch communication plan named?",
    optionsSummary: "Named owner + message + timeline defined / Owner named — message and timeline not set / Not discussed",
    recommendation: "ADD",
    rationale: "The field learns about a new system from their manager on launch day. No comms plan = adoption collapse on Day 1.",
    hasHardStop: false,
  },
  {
    id: "cp4_n03",
    cp: 4,
    type: "NEW",
    questionKey: null,
    label: "Frontline manager buy-in confirmed?",
    optionsSummary: "Briefed and expressed buy-in / Partial — some engaged / Not yet engaged / Active resistance (Review)",
    recommendation: "ADD",
    rationale: "Exec sponsorship closes the contract. Frontline manager buy-in determines whether the system sticks on the floor. The two are independent.",
    hasHardStop: false,
  },
  {
    id: "cp4_n04",
    cp: 4,
    type: "NEW",
    questionKey: null,
    label: "IT resource commitment confirmed for implementation?",
    optionsSummary: "Named IT resource with confirmed availability / IT informed and willing — no formal commitment / IT not engaged or deprioritizing / IT is the blocker (Disqualify)",
    recommendation: "ADD",
    rationale: "Many manufacturing deals stall post-signature because IT is not resourced for the project. This tests capacity, not just willingness.",
    hasHardStop: true,
  },
  {
    id: "cp4_n05",
    cp: 4,
    type: "NEW",
    questionKey: null,
    label: "Rollout scope — single site, multi-site, or enterprise-wide?",
    optionsSummary: "Single site (OK) / Multi-site 2–5 locations (OK) / Enterprise-wide 6+ locations (flag for scoping) / Scope not yet defined",
    recommendation: "ADD",
    rationale: "Single-site pilot and enterprise rollout across 14 plants have fundamentally different change management requirements. Knowing scope at CP4 allows us to size implementation correctly.",
    hasHardStop: false,
  },

  // =========================================================================
  // CP 5 — Timeline
  // =========================================================================
  {
    id: "cp5_e01",
    cp: 5,
    type: "EXISTING",
    questionKey: "cp5_cloud_readiness",
    label: "Cloud readiness?",
    optionsSummary: "Cloud OK / Hybrid / On-prem only / Cloud banned (Disqualify)",
    recommendation: "KEEP",
    rationale: "Cloud-banned is already a hard kill. This question surfaces it early.",
    hasHardStop: true,
  },
  {
    id: "cp5_e02",
    cp: 5,
    type: "EXISTING",
    questionKey: "cp5_erp_integration",
    label: "ERP / financial system integration confirmed?",
    optionsSummary: "Specified — system + version known / Unknown / Blocked by IT policy",
    recommendation: "KEEP",
    rationale: "ERP system and version must be confirmed before technical scoping begins.",
    hasHardStop: false,
  },
  {
    id: "cp5_e03",
    cp: 5,
    type: "EXISTING",
    questionKey: "cp5_it_engaged",
    label: "IT stakeholder engaged?",
    optionsSummary: "Yes — named contact / Loose engagement / No",
    recommendation: "KEEP",
    rationale: "IT engagement is required for integration, security, and data work. Anonymous IT engagement is not engagement.",
    hasHardStop: false,
  },
  {
    id: "cp5_e04",
    cp: 5,
    type: "EXISTING",
    questionKey: "cp5_data_quality",
    label: "Data environment quality?",
    optionsSummary: "Clean — well-governed / Mixed / Chaotic",
    recommendation: "EDIT",
    rationale: "Reframe to 'Will the buyer's data work for our system on Day 1, or is a data prep project required first?' — decision-relevant version of the same question.",
    hasHardStop: false,
  },
  {
    id: "cp5_e05",
    cp: 5,
    type: "EXISTING",
    questionKey: "cp5_integration_timeline",
    label: "ERP integration — scheduled and owned?",
    optionsSummary: "Scheduled — named owner, timeline agreed / Named owner — no timeline / No owner unknown / No integration required",
    recommendation: "KEEP",
    rationale: "ERP integration being on someone's project plan with a date is different from knowing the system exists.",
    hasHardStop: false,
  },
  {
    id: "cp5_e06",
    cp: 5,
    type: "EXISTING",
    questionKey: "cp5_security_review_status",
    label: "Security / infosec review initiated?",
    optionsSummary: "Completed clean / In progress / Required not started / Not required / Required — known blocker (Disqualify)",
    recommendation: "KEEP",
    rationale: "Hard stop on known unresolved security blocker. This question is the only security gate in the current bank.",
    hasHardStop: true,
  },
  // CP5 new
  {
    id: "cp5_n01",
    cp: 5,
    type: "NEW",
    questionKey: null,
    label: "Identity / SSO requirements known?",
    optionsSummary: "SSO confirmed compatible / SSO required — compatibility not verified / SSO required — provider unknown / Not required / SSO required — we do not support their provider (Disqualify)",
    recommendation: "ADD",
    rationale: "SSO is a distinct workstream from the security review. Often a separate IT owner and timeline. Frequent hard blocker in regulated manufacturing (aerospace, medical device).",
    hasHardStop: true,
  },
  {
    id: "cp5_n02",
    cp: 5,
    type: "NEW",
    questionKey: null,
    label: "Plant-floor connectivity confirmed?",
    optionsSummary: "Cloud-connected or confirmed pipeline (OK) / Hybrid — migration plan in place / OT/IT separation — path unclear / Air-gapped shop floor (Disqualify)",
    recommendation: "ADD",
    rationale: "Many manufacturing environments have shop-floor networks that are air-gapped or running on legacy OT protocols. Most common technical disqualifier in heavy manufacturing and aerospace not covered by existing questions.",
    hasHardStop: true,
  },
  {
    id: "cp5_n03",
    cp: 5,
    type: "NEW",
    questionKey: null,
    label: "Regulatory / compliance data residency requirement?",
    optionsSummary: "No requirements (OK) / Requirements identified — we can meet them / Requirements identified — compatibility unverified / Requirements identified — we cannot meet them (Disqualify)",
    recommendation: "ADD",
    rationale: "Medical device (FDA 21 CFR Part 11), aerospace (ITAR, CMMC), and some automotive (TISAX) environments have data residency rules that can disqualify a cloud deployment.",
    hasHardStop: true,
  },

  // =========================================================================
  // CP 6 — Authority (12 questions, no changes)
  // =========================================================================
  {
    id: "cp6_e01",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_real_demo_run",
    label: "Was a real demo actually run?",
    optionsSummary: "Live product walked through / Recorded walkthrough / Slides only (Review) / Not run yet / Buyer refused (Disqualify)",
    recommendation: "KEEP",
    rationale: "A real demo means a working product shown to the buyer's people — not slides, not a recording.",
    hasHardStop: true,
  },
  {
    id: "cp6_e02",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_must_have_list",
    label: "Pre-demo must-have list captured?",
    optionsSummary: "Yes — buyer signed off / Yes — internal only / Partial / None (Review)",
    recommendation: "KEEP",
    rationale: "Without a must-have list, you cannot judge whether the demo improved the situation.",
    hasHardStop: false,
  },
  {
    id: "cp6_e03",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_must_have_count",
    label: "How many must-have requirements are on the list?",
    optionsSummary: "3–6 (healthy) / 1–2 (thin) / 7–12 (heavy) / 13+ (likely RFP) / None listed (Review)",
    recommendation: "KEEP",
    rationale: "Requirement count drives gap risk. 13+ suggests RFP treatment; a phase-break conversation is needed.",
    hasHardStop: false,
  },
  {
    id: "cp6_e04",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_right_stakeholders",
    label: "Were the right stakeholders in the demo?",
    optionsSummary: "EB + user owner + IT/Ops all attended / User + IT no EB / User-side only / Wrong room (Review)",
    recommendation: "KEEP",
    rationale: "Missing the economic buyer makes the demo verdict non-binding.",
    hasHardStop: false,
  },
  {
    id: "cp6_e05",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_scripted_to_use_case",
    label: "Was the demo scripted to their actual use case?",
    optionsSummary: "Their workflow + named roles + data fields / Hybrid / Standard demo path",
    recommendation: "KEEP",
    rationale: "A standard demo path is not a demonstration of fit — it is a product tour.",
    hasHardStop: false,
  },
  {
    id: "cp6_e06",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_must_have_confirmed",
    label: "Of the must-haves, how many were confirmed in the demo?",
    optionsSummary: "All confirmed / All but one partial / Some partial / Most partial or no (Review) / Demo did not cover must-haves (Review)",
    recommendation: "KEEP",
    rationale: "'Confirmed' means buyer saw it working and acknowledged fit. Roadmap promises do not count.",
    hasHardStop: false,
  },
  {
    id: "cp6_e07",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_open_gaps_count",
    label: "How many must-have gaps are still open after the demo?",
    optionsSummary: "Zero gaps / 1 gap with path / 1 gap no path (Review) / 2+ gaps (Review)",
    recommendation: "KEEP",
    rationale: "A must-have gap we cannot close is a signal to walk, not a negotiation point.",
    hasHardStop: false,
  },
  {
    id: "cp6_e08",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_verdict",
    label: "Post-demo verdict — did this improve our situation?",
    optionsSummary: "Improved / Same / Worse — nice-to-have gap / Worse — must-have gap we cannot close (Disqualify)",
    recommendation: "KEEP",
    rationale: "Bryan's frame: did the demo improve, leave us flat, or surface a disqualifying gap on a must-have?",
    hasHardStop: true,
  },
  {
    id: "cp6_e09",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_buyer_next_step",
    label: "Did the buyer name the next step coming out of the demo?",
    optionsSummary: "Yes — specific next step with owner and date / General interest / No clear signal (Review)",
    recommendation: "KEEP",
    rationale: "A real buyer leaves a successful demo with the next step on their lips. Silence means it did not move them.",
    hasHardStop: false,
  },
  {
    id: "cp6_e10",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_followup_doc",
    label: "Was the demo follow-up captured in writing?",
    optionsSummary: "Sent and acknowledged / Sent no acknowledgment / Not sent",
    recommendation: "KEEP",
    rationale: "Confirmed must-haves, open gaps, and next step — sent back within 48 hours. This is what makes the verdict binding.",
    hasHardStop: false,
  },
  {
    id: "cp6_e11",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_capability_misrepresented",
    label: "Was any capability misrepresented or oversold in the demo?",
    optionsSummary: "Clean / Corrected in follow-up / Uncorrected misrepresentation on a must-have (Disqualify)",
    recommendation: "KEEP",
    rationale: "Uncaught misrepresentation kills the deal at legal. If it happened, correct it in writing immediately.",
    hasHardStop: true,
  },
  {
    id: "cp6_e12",
    cp: 6,
    type: "EXISTING",
    questionKey: "cp6_buyer_engagement",
    label: "Buyer engagement during the demo?",
    optionsSummary: "High — specific questions, named team members / Medium — polite / Low — watched passively (Review)",
    recommendation: "KEEP",
    rationale: "Disengagement is a signal, not a courtesy. A passively-watching room did not buy into what they saw.",
    hasHardStop: false,
  },

  // =========================================================================
  // CP 7 — Authority
  // =========================================================================
  {
    id: "cp7_e01",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_budget_confirmed",
    label: "Budget confirmed?",
    optionsSummary: "Named line item / approved / General budget not earmarked / Unfunded (Review)",
    recommendation: "KEEP",
    rationale: "Named line item is the only budget signal worth forecasting on.",
    hasHardStop: false,
  },
  {
    id: "cp7_e02",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_capex_opex",
    label: "CapEx vs. OpEx classification?",
    optionsSummary: "CapEx / OpEx / Unknown",
    recommendation: "REMOVE",
    rationale: "Operationally interesting, not qualify/disqualify. CapEx vs. OpEx affects close mechanics — not whether to keep working the deal.",
    hasHardStop: false,
  },
  {
    id: "cp7_e03",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_within_range",
    label: "Price within budget range?",
    optionsSummary: "Within range / Stretch / Out of range",
    recommendation: "KEEP",
    rationale: "Out-of-range is not automatically disqualifying but must be flagged for manager conversation.",
    hasHardStop: false,
  },
  {
    id: "cp7_e04",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_procurement_path",
    label: "Procurement / approval path?",
    optionsSummary: "Mapped — every step known / Partial mapping / Unknown",
    recommendation: "KEEP",
    rationale: "Unknown procurement path at CP7 means the close date is fictional.",
    hasHardStop: false,
  },
  {
    id: "cp7_e05",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_legal_msa",
    label: "Legal / MSA status?",
    optionsSummary: "MSA executed / Drafted under review / Not started",
    recommendation: "MOVE-IN",
    rationale: "This is a paper/legal question. It belongs at CP10, not CP7. Move it to where written commitment and contract status live.",
    hasHardStop: false,
  },
  {
    id: "cp7_e06",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_cost_inaction_anchor",
    label: "Cost-of-inaction anchor set?",
    optionsSummary: "Yes — anchored with a number / Qualitative anchor / No anchor (Review)",
    recommendation: "KEEP",
    rationale: "Without a cost-of-inaction anchor the ROI case is defenseless.",
    hasHardStop: false,
  },
  {
    id: "cp7_e07",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_roi_case",
    label: "ROI case built?",
    optionsSummary: "Signed off by buyer / Drafted unsigned / None (Review)",
    recommendation: "KEEP",
    rationale: "An unsigned ROI case is a working document. A buyer-signed ROI case is a commitment to the premise.",
    hasHardStop: false,
  },
  {
    id: "cp7_e08",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_budget_period",
    label: "Budget in-period or future-year?",
    optionsSummary: "In-period — current quarter / Next quarter / Next fiscal year (Review) / Unknown",
    recommendation: "KEEP",
    rationale: "Next-fiscal-year budget carries fundamentally different forecast weight than an approved current-period spend.",
    hasHardStop: false,
  },
  {
    id: "cp7_e09",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_eb_spend_authority",
    label: "Economic buyer's unilateral spend authority confirmed?",
    optionsSummary: "EB has unilateral authority / One additional approval required / Committee or board required / Unknown",
    recommendation: "KEEP",
    rationale: "Budget existing and EB being able to approve it unilaterally are different questions.",
    hasHardStop: false,
  },
  {
    id: "cp7_e10",
    cp: 7,
    type: "EXISTING",
    questionKey: "cp7_financial_viability",
    label: "Financial viability of the buyer?",
    optionsSummary: "Strong / Adequate / Unverified / Distressed (Review) / Bankruptcy filed (Disqualify) / Insolvent (Disqualify)",
    recommendation: "KEEP",
    rationale: "Bankruptcy or insolvency is a hard kill — procurement is frozen and no commercial path exists.",
    hasHardStop: true,
  },
  // CP7 new
  {
    id: "cp7_n01",
    cp: 7,
    type: "NEW",
    questionKey: null,
    label: "Has anyone at this account bought from us at this price point before?",
    optionsSummary: "Yes — similar or larger on record / New price point — narrative built / Significantly above any prior — no narrative (Review)",
    recommendation: "ADD",
    rationale: "If we are 3x our highest deal at this account, the price needs a justification story we may not have. Not about discounting — about whether the AE can defend the number.",
    hasHardStop: false,
  },
  {
    id: "cp7_n02",
    cp: 7,
    type: "NEW",
    questionKey: null,
    label: "Competitive pricing — do we know what alternatives will cost them?",
    optionsSummary: "Yes — know competitive price range or seen proposals / Partial — interest mentioned, no numbers / Unknown / Using us as price leverage against chosen competitor (Review)",
    recommendation: "ADD",
    rationale: "A buyer who has not been quoted by a competitor is not actually evaluating alternatives. Knowing competitive pricing lets the AE detect when they are being used as a stalking horse.",
    hasHardStop: false,
  },

  // =========================================================================
  // CP 8 — Competition
  // =========================================================================
  {
    id: "cp8_e01",
    cp: 8,
    type: "EXISTING",
    questionKey: "cp8_close_date",
    label: "Mutual close date?",
    optionsSummary: "Joint mutual close date in writing / Our date — buyer has not agreed / Unknown",
    recommendation: "KEEP",
    rationale: "A close date only on our side is a CRM number, not a mutual commitment.",
    hasHardStop: false,
  },
  {
    id: "cp8_e02",
    cp: 8,
    type: "EXISTING",
    questionKey: "cp8_compelling_event",
    label: "Firm or formal timeline?",
    optionsSummary: "Formal timeline (external + dated + significant consequence) / Firm/formal timeline / Target date / No firm date (Review)",
    recommendation: "KEEP",
    rationale: "Formal timelines cannot be manufactured. Firm/formal is the realistic baseline. No date means no close.",
    hasHardStop: false,
  },
  {
    id: "cp8_e03",
    cp: 8,
    type: "EXISTING",
    questionKey: "cp8_map",
    label: "Mutual Action Plan (MAP)?",
    optionsSummary: "Signed by buyer / Drafted — not signed / None",
    recommendation: "KEEP",
    rationale: "A signed MAP is the most reliable close-date predictor in the bank.",
    hasHardStop: false,
  },
  {
    id: "cp8_e04",
    cp: 8,
    type: "EXISTING",
    questionKey: "cp8_next_steps",
    label: "Next steps booked?",
    optionsSummary: "On calendar with accepted invite / Verbal only / None",
    recommendation: "REMOVE",
    rationale: "Overlaps with cp8_map. A signed MAP already defines next steps. Redundant question.",
    hasHardStop: false,
  },
  {
    id: "cp8_e05",
    cp: 8,
    type: "EXISTING",
    questionKey: "cp8_customer_resources",
    label: "Customer resources committed?",
    optionsSummary: "Named owners on their side / Partial commitment / None",
    recommendation: "KEEP",
    rationale: "If the buyer has not committed named resources to the project, the implementation will stall on Day 1.",
    hasHardStop: false,
  },
  {
    id: "cp8_e06",
    cp: 8,
    type: "EXISTING",
    questionKey: "cp8_rfp_branch",
    label: "RFP branch — if RFP, did we shape it?",
    optionsSummary: "We shaped the RFP / RFP — we did not shape / Not an RFP",
    recommendation: "KEEP",
    rationale: "An RFP we did not shape is a 10% win probability. The AE needs to know this and escalate.",
    hasHardStop: false,
  },
  {
    id: "cp8_e07",
    cp: 8,
    type: "EXISTING",
    questionKey: "cp8_ce_corroboration",
    label: "Timeline confirmed by someone other than the champion?",
    optionsSummary: "Confirmed by EB or second senior stakeholder / Confirmed by second stakeholder / Champion only / No second source sought (Review)",
    recommendation: "KEEP",
    rationale: "A timeline only the champion cites is a timeline only the champion believes.",
    hasHardStop: false,
  },
  {
    id: "cp8_e08",
    cp: 8,
    type: "EXISTING",
    questionKey: "cp8_slip_consequence",
    label: "What happens if this deal slips one quarter?",
    optionsSummary: "Named consequence — specific financial/compliance/operational / Inconvenient but no specific consequence / Not asked / No material impact (Review)",
    recommendation: "KEEP",
    rationale: "Distinguishes a real deadline from an aspirational one. A buyer who cannot name a slip consequence has no forcing function.",
    hasHardStop: false,
  },
  // CP8 new
  {
    id: "cp8_n01",
    cp: 8,
    type: "NEW",
    questionKey: null,
    label: "Are we tracking ahead of, on, or behind the buyer's stated timeline?",
    optionsSummary: "Ahead — buyer pulling us forward / On timeline — milestones landing / One step behind — recoverable / Multiple milestones missed (Review)",
    recommendation: "ADD",
    rationale: "Tests pace, not just existence of a timeline. Allows a manager to see a deal drifting in real time, not at end-of-quarter review.",
    hasHardStop: false,
  },
  {
    id: "cp8_n02",
    cp: 8,
    type: "NEW",
    questionKey: null,
    label: "Implementation start date — has the buyer named one?",
    optionsSummary: "Specific start date named and in MAP / Window discussed — not a specific date / No start date yet / Depends on factors outside buyer's control (Review)",
    recommendation: "ADD",
    rationale: "A close date without an implementation start date is a number on a spreadsheet. Buyer willingness to name the date is the best signal the commitment is real.",
    hasHardStop: false,
  },

  // =========================================================================
  // CP 9 — Finance
  // =========================================================================
  {
    id: "cp9_e01",
    cp: 9,
    type: "EXISTING",
    questionKey: "cp9_landscape_known",
    label: "Competitive landscape known?",
    optionsSummary: "Named all competitors / Named some / Blind",
    recommendation: "KEEP",
    rationale: "Competing blind is a forecast risk. If the AE does not know the field, the champion has not been coached to defend against it.",
    hasHardStop: false,
  },
  {
    id: "cp9_e02",
    cp: 9,
    type: "EXISTING",
    questionKey: "cp9_preferred_status",
    label: "Our preferred status?",
    optionsSummary: "Confirmed by buyer / Signaled — not confirmed / Unknown / Not preferred (Review)",
    recommendation: "KEEP",
    rationale: "Champion-reported preferred status is assumption. Buyer-confirmed preferred status is a qualification signal.",
    hasHardStop: false,
  },
  {
    id: "cp9_e03",
    cp: 9,
    type: "EXISTING",
    questionKey: "cp9_decision_criteria",
    label: "Decision criteria mapped and weighted?",
    optionsSummary: "We wrote them with the buyer / They wrote them — we know weights / Unknown",
    recommendation: "KEEP",
    rationale: "Writing the decision criteria with the buyer is the single strongest preference signal at CP9.",
    hasHardStop: false,
  },
  {
    id: "cp9_e04",
    cp: 9,
    type: "EXISTING",
    questionKey: "cp9_status_quo_risk",
    label: "Is 'do nothing' a credible outcome?",
    optionsSummary: "No — status quo explicitly unacceptable / Low risk / Moderate risk / High risk — deferral likely (Review)",
    recommendation: "KEEP",
    rationale: "In manufacturing enterprise deals the primary competitor is often inaction. The existing questions cover named vendors but miss the status quo threat.",
    hasHardStop: false,
  },
  {
    id: "cp9_e05",
    cp: 9,
    type: "EXISTING",
    questionKey: "cp9_eb_competitive_view",
    label: "Economic buyer's competitive preference known?",
    optionsSummary: "EB stated preference for us / Champion reports EB favors us / EB view not surfaced / EB prefers competitor or status quo (Review)",
    recommendation: "KEEP",
    rationale: "The champion may know the competitive landscape. The economic buyer's opinion determines the outcome.",
    hasHardStop: false,
  },
  // CP9 new
  {
    id: "cp9_n01",
    cp: 9,
    type: "NEW",
    questionKey: null,
    label: "Have we shown why we are meaningfully different — not just better — for this buyer specifically?",
    optionsSummary: "Yes — buyer-specific differentiation story, validated by champion / Partial — generic narrative / No — competing on features and price only (Review)",
    recommendation: "ADD",
    rationale: "Many manufacturing deals are lost not on price but on undifferentiation. The buyer cannot tell us apart from the alternative. Generic 'we're better' claims do not win committee decisions.",
    hasHardStop: false,
  },
  {
    id: "cp9_n02",
    cp: 9,
    type: "NEW",
    questionKey: null,
    label: "Has the buyer evaluated an internal-build option?",
    optionsSummary: "Yes — evaluated and chose not to pursue / Yes — still being considered alongside us / No — never discussed / Yes — leaning toward internal build (Review)",
    recommendation: "ADD",
    rationale: "For manufacturing buyers — especially in automotive and aerospace — 'we'll build it ourselves with the engineering team' is a real alternative, often the primary competitor.",
    hasHardStop: false,
  },
  {
    id: "cp9_n03",
    cp: 9,
    type: "NEW",
    questionKey: null,
    label: "Win theme — does our champion know why we win?",
    optionsSummary: "Yes — champion articulates win theme in their own words / Partial — supportive but defaults to generic product language / No — champion not yet coached",
    recommendation: "ADD",
    rationale: "The champion sells for us internally without us present. If they cannot articulate in one sentence why we win for this buyer, they will not be effective in committee.",
    hasHardStop: false,
  },
  {
    id: "cp9_n04",
    cp: 9,
    type: "NEW",
    questionKey: null,
    label: "Have we reviewed our loss patterns and confirmed none apply here?",
    optionsSummary: "Loss patterns reviewed — none apply or mitigations in place / Partial — not all reviewed / No — loss patterns not reviewed against this deal",
    recommendation: "ADD",
    rationale: "Every AE knows our common loss patterns. A deal that matches our standard loss pattern without a flagged manager conversation is a forecast risk.",
    hasHardStop: false,
  },

  // =========================================================================
  // CP 10 — Legal
  // =========================================================================
  {
    id: "cp10_e01",
    cp: 10,
    type: "EXISTING",
    questionKey: "cp10_written_commit",
    label: "Written commitment received?",
    optionsSummary: "Signed / Verbal only / No commit (Disqualify)",
    recommendation: "KEEP",
    rationale: "The hard stop on 'no commit' is correct. Verbal commitments do not close.",
    hasHardStop: true,
  },
  {
    id: "cp10_e02",
    cp: 10,
    type: "EXISTING",
    questionKey: "cp10_commitment_type",
    label: "What form is the written commitment?",
    optionsSummary: "Signed order form or PO / Signed MSA with SOW pending / Signed LOI / Signed mutual close plan only",
    recommendation: "KEEP",
    rationale: "All written commitments are not equal for forecast purposes. Form matters.",
    hasHardStop: false,
  },
  // CP10: move-in from CP7
  {
    id: "cp10_n01",
    cp: 10,
    type: "NEW",
    questionKey: "cp7_legal_msa",
    label: "Legal / MSA status? (moved from CP7 — this is a paper question, not a budget question)",
    optionsSummary: "MSA executed / Drafted — under review / Not started",
    recommendation: "MOVE-IN",
    rationale: "At CP7, MSA status is a forecast question. At CP10, it is a gate question. Moving it here puts it in the right context.",
    hasHardStop: false,
  },
  {
    id: "cp10_n02",
    cp: 10,
    type: "NEW",
    questionKey: null,
    label: "Counter-signed by both sides?",
    optionsSummary: "Yes — countersigned, documentation on file / We signed — buyer signature in process / We signed — buyer signature not yet requested (Review) / We signed — buyer needs additional internal approval (Review)",
    recommendation: "ADD",
    rationale: "A one-sided signed commitment is not a deal. Many deals die between the AE sending the document and the buyer's countersign — especially when the buyer's legal or board adds a review step.",
    hasHardStop: false,
  },
  {
    id: "cp10_n03",
    cp: 10,
    type: "NEW",
    questionKey: null,
    label: "Implementation kickoff date confirmed post-signature?",
    optionsSummary: "Yes — confirmed, in system, resources assigned / Date proposed — not agreed by both sides / No kickoff date — buyer will circle back (Review)",
    recommendation: "ADD",
    rationale: "Paper signed without a kickoff date is a forecast win, not a delivered outcome. In manufacturing, kickoff competes with production schedules and fiscal year transitions.",
    hasHardStop: false,
  },
  {
    id: "cp10_n04",
    cp: 10,
    type: "NEW",
    questionKey: null,
    label: "Has the buyer named who will own the relationship post-signature on their side?",
    optionsSummary: "Named post-signature owner briefed / Champion owns it — formally confirmed / Champion owns it — not formally confirmed / Unknown (Review)",
    recommendation: "ADD",
    rationale: "Many manufacturing deals fail in the first 90 days because the champion moves on and no one has named who owns the vendor relationship. Post-signature owner is often different from the champion.",
    hasHardStop: false,
  },
  {
    id: "cp10_n05",
    cp: 10,
    type: "NEW",
    questionKey: null,
    label: "Services / implementation scoping document signed?",
    optionsSummary: "SOW or scoping doc signed / SOW in draft — being negotiated / Services scoping not yet started / Buyer resisting formal SOW (Review)",
    recommendation: "ADD",
    rationale: "A signed order form without a signed SOW means scope creep begins on Day 1 and the services team has no contractual ground to stand on.",
    hasHardStop: false,
  },
];

// ---------------------------------------------------------------------------
// Storage helpers (localStorage cache + server)
// ---------------------------------------------------------------------------

const STORAGE_KEY = "aip:cp210-audit-v2:v1";
const AUDIT_VERSION = "v2";

function emptyState(): AuditStateV2 {
  return { decisions: {}, userAdded: {}, startedAt: new Date().toISOString() };
}

function loadState(): AuditStateV2 {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return {
        decisions: parsed.decisions ?? {},
        userAdded: parsed.userAdded ?? {},
        startedAt: parsed.startedAt ?? new Date().toISOString(),
      };
    }
  } catch {}
  return emptyState();
}

function saveState(s: AuditStateV2) {
  // 1. localStorage cache (immediate)
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
  // 2. Server write (fire-and-forget — localStorage is offline fallback)
  fetch("/api/audit-review", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      version:    AUDIT_VERSION,
      decisions:  s.decisions,
      user_added: s.userAdded,
      started_at: s.startedAt,
    }),
  }).catch(() => {
    // Server write failed — data safe in localStorage cache.
  });
}

/** Hydrate state from server on mount, falling back to localStorage cache. */
async function loadStateFromServer(): Promise<AuditStateV2 | null> {
  try {
    const res = await fetch(`/api/audit-review?version=${AUDIT_VERSION}`, { credentials: "include" });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.session) return null;
    return {
      decisions:  json.session.decisions  ?? {},
      userAdded:  json.session.user_added ?? {},
      startedAt:  json.session.started_at ?? new Date().toISOString(),
    } as AuditStateV2;
  } catch {
    return null;
  }
}

// Generate a simple unique ID for user-added questions
function uid(): string {
  return `ua_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AuditReviewV2() {
  const [state, setState] = useState<AuditStateV2>(() => loadState());
  const [editTexts, setEditTexts] = useState<Record<string, string>>({});
  const [noteTexts, setNoteTexts] = useState<Record<string, string>>({});
  const [openEditCard, setOpenEditCard] = useState<string | null>(null);

  const gateRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // On mount: hydrate from server (server is source of truth; localStorage is offline cache).
  useEffect(() => {
    loadStateFromServer().then((serverState) => {
      if (!serverState) return;
      setState((local) => {
        const serverCount = Object.keys(serverState.decisions).length;
        const localCount  = Object.keys(local.decisions).length;
        if (serverCount >= localCount) return serverState;
        return local;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on every state change
  useEffect(() => { saveState(state); }, [state]);

  // ---------------------------------------------------------------------------
  // Cards grouped by gate
  // ---------------------------------------------------------------------------
  const cardsByGate = useMemo<Record<number, AuditCard[]>>(() => {
    const map: Record<number, AuditCard[]> = {};
    GATES.forEach((cp) => { map[cp] = []; });
    ALL_CARDS.forEach((c) => { map[c.cp]?.push(c); });
    return map;
  }, []);

  // ---------------------------------------------------------------------------
  // Decision helpers
  // ---------------------------------------------------------------------------
  function setDecision(id: string, decision: AuditDecision) {
    setState((s) => ({
      ...s,
      decisions: {
        ...s.decisions,
        [id]: {
          decision,
          editedText: decision === "edit" ? (editTexts[id] ?? "") : s.decisions[id]?.editedText,
          notes: noteTexts[id] ?? s.decisions[id]?.notes,
          decidedAt: new Date().toISOString(),
        },
      },
    }));
    if (decision === "edit") {
      setOpenEditCard(id);
    } else {
      setOpenEditCard(null);
    }
  }

  function clearDecision(id: string) {
    setState((s) => {
      const next = { ...s.decisions };
      delete next[id];
      return { ...s, decisions: next };
    });
    setOpenEditCard(null);
  }

  function saveEditText(id: string, text: string) {
    setEditTexts((e) => ({ ...e, [id]: text }));
    setState((s) => ({
      ...s,
      decisions: {
        ...s.decisions,
        [id]: {
          ...(s.decisions[id] ?? { decision: "edit", decidedAt: new Date().toISOString() }),
          editedText: text,
        },
      },
    }));
  }

  function saveNote(id: string, note: string) {
    setNoteTexts((n) => ({ ...n, [id]: note }));
    setState((s) => ({
      ...s,
      decisions: {
        ...s.decisions,
        [id]: {
          ...(s.decisions[id] ?? { decision: "skip", decidedAt: new Date().toISOString() }),
          notes: note,
        },
      },
    }));
  }

  // ---------------------------------------------------------------------------
  // User-added question helpers
  // ---------------------------------------------------------------------------
  function addUserQuestion(cp: number) {
    const key = String(cp);
    const newQ: UserAddedQuestion = {
      id: uid(),
      prompt: "",
      rationale: "",
      options: "",
      createdAt: new Date().toISOString(),
    };
    setState((s) => ({
      ...s,
      userAdded: {
        ...s.userAdded,
        [key]: [...(s.userAdded[key] ?? []), newQ],
      },
    }));
  }

  function updateUserQuestion(cp: number, id: string, field: keyof UserAddedQuestion, value: string) {
    const key = String(cp);
    setState((s) => ({
      ...s,
      userAdded: {
        ...s.userAdded,
        [key]: (s.userAdded[key] ?? []).map((q) =>
          q.id === id ? { ...q, [field]: value } : q
        ),
      },
    }));
  }

  function removeUserQuestion(cp: number, id: string) {
    const key = String(cp);
    setState((s) => ({
      ...s,
      userAdded: {
        ...s.userAdded,
        [key]: (s.userAdded[key] ?? []).filter((q) => q.id !== id),
      },
    }));
  }

  function resetAll() {
    if (!window.confirm("Reset all decisions and user-added questions? This cannot be undone.")) return;
    setState(emptyState());
    setEditTexts({});
    setNoteTexts({});
    setOpenEditCard(null);
  }

  // ---------------------------------------------------------------------------
  // Progress — denominates only on existing catalog cards, not user-added
  // ---------------------------------------------------------------------------
  const totalCards = ALL_CARDS.length;
  const reviewedCount = useMemo(
    () => Object.keys(state.decisions).filter((id) => ALL_CARDS.some((c) => c.id === id)).length,
    [state.decisions]
  );
  const pct = totalCards ? Math.round((reviewedCount / totalCards) * 100) : 0;
  const approvedCount = useMemo(
    () => Object.values(state.decisions).filter((d) => d.decision === "keep" || d.decision === "replace").length,
    [state.decisions]
  );
  const editCount = useMemo(
    () => Object.values(state.decisions).filter((d) => d.decision === "edit").length,
    [state.decisions]
  );
  const skippedCount = useMemo(
    () => Object.values(state.decisions).filter((d) => d.decision === "skip").length,
    [state.decisions]
  );

  // Per-gate progress
  function gateReviewed(cp: number): number {
    return (cardsByGate[cp] ?? []).filter((c) => state.decisions[c.id]).length;
  }
  function gateTotal(cp: number): number {
    return (cardsByGate[cp] ?? []).length;
  }

  // ---------------------------------------------------------------------------
  // Scroll helpers
  // ---------------------------------------------------------------------------
  function scrollToGate(cp: number) {
    const el = gateRefs.current[cp];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // ---------------------------------------------------------------------------
  // Download
  // ---------------------------------------------------------------------------
  function downloadDecisions() {
    const payload = {
      exportedAt: new Date().toISOString(),
      startedAt: state.startedAt,
      version: "audit-v2",
      summary: {
        total_catalog_cards: totalCards,
        reviewed: reviewedCount,
        kept: Object.values(state.decisions).filter((d) => d.decision === "keep").length,
        replaced: Object.values(state.decisions).filter((d) => d.decision === "replace").length,
        edited: editCount,
        skipped: skippedCount,
        total_user_added: Object.values(state.userAdded).reduce((acc, arr) => acc + arr.length, 0),
      },
      decisions: state.decisions,
      cards: ALL_CARDS.map((c) => ({
        ...c,
        decision: state.decisions[c.id] ?? null,
      })),
      userAdded: state.userAdded,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cp2-10-audit-v2-decisions-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div style={{ minHeight: "100vh", background: "#F9FAFB", padding: "24px 16px" }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>

        {/* Product-generic banner */}
        <div style={{
          background: "#FAFAF7",
          border: "1px solid #E5E5E7",
          borderRadius: 8,
          padding: "10px 16px",
          marginBottom: 18,
          fontSize: 13,
          color: "#0B0B0F",
          lineHeight: 1.5,
        }}>
          <strong>All questions are phrased product-generic.</strong>{" "}
          The "Which module(s) is this for?" multi-selector lives on the BDR discovery screen (CP1) — Profit+ is the default, all six modules are supported, and the selection propagates to every downstream gate.
        </div>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>
              Manager — CP2 to CP10 Full Audit Review
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: "#111827", margin: "0 0 6px 0", lineHeight: 1.2 }}>
              Every question, checkpoint by checkpoint
            </h1>
            <p style={{ fontSize: 14, color: "#6B7280", margin: 0, maxWidth: 720, lineHeight: 1.6 }}>
              Every existing question plus every proposed addition, in order. For each card: Keep, Replace, Edit text, or Skip.
              Add your own questions at any checkpoint using the button at the bottom of each section.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button
              onClick={downloadDecisions}
              style={{
                padding: "9px 18px",
                background: "#111827",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Download JSON
            </button>
            <Link href="/pilot">
              <a style={{ fontSize: 13, color: "#374151", textDecoration: "none", padding: "9px 14px", border: "1px solid #D1D5DB", borderRadius: 7, background: "#fff", whiteSpace: "nowrap" }}>
                Back to home
              </a>
            </Link>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "14px 18px", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 7, flexWrap: "wrap", gap: 4 }}>
            <span style={{ color: "#374151" }}>
              Reviewed: <strong>{reviewedCount}</strong> of {totalCards} catalog cards
              {" "}<span style={{ color: "#9CA3AF", fontWeight: 400 }}>({pct}%)</span>
            </span>
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>
              {approvedCount} kept/replaced · {editCount} edited · {skippedCount} skipped
              {Object.values(state.userAdded).some((a) => a.length > 0) && (
                <> · {Object.values(state.userAdded).reduce((acc, a) => acc + a.length, 0)} user-added</>
              )}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, height: 6, background: "#E5E7EB", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: "#16A34A", transition: "width 0.3s" }} />
            </div>
            <button
              onClick={resetAll}
              style={{ fontSize: 12, padding: "5px 10px", background: "#fff", color: "#991B1B", border: "1px solid #FCA5A5", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Reset all
            </button>
          </div>
        </div>

        {/* Gate jump tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 28, flexWrap: "wrap" }}>
          {GATES.map((cp) => {
            const reviewed = gateReviewed(cp);
            const total = gateTotal(cp);
            const allDone = total > 0 && reviewed === total;
            const userAddedCount = (state.userAdded[String(cp)] ?? []).length;
            return (
              <button
                key={cp}
                onClick={() => scrollToGate(cp)}
                style={{
                  padding: "8px 12px",
                  background: "#fff",
                  color: allDone ? "#065F46" : "#374151",
                  border: `1px solid ${allDone ? "#86EFAC" : "#D1D5DB"}`,
                  borderRadius: 7,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 500,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 115,
                  transition: "border-color 0.2s",
                }}
              >
                <span style={{ fontSize: 10, color: "#9CA3AF", marginBottom: 1 }}>CP{cp}</span>
                <span style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{GATE_NAMES[cp]}</span>
                <span style={{ fontSize: 10, color: allDone ? "#065F46" : "#6B7280" }}>
                  {reviewed}/{total}{allDone ? " done" : ""}
                  {userAddedCount > 0 && <> · +{userAddedCount} mine</>}
                </span>
              </button>
            );
          })}
        </div>

        {/* Gate sections */}
        {GATES.map((cp) => {
          const cards = cardsByGate[cp] ?? [];
          const userQuestions = state.userAdded[String(cp)] ?? [];

          return (
            <div
              key={cp}
              ref={(el) => { gateRefs.current[cp] = el; }}
              style={{ marginBottom: 48, scrollMarginTop: 20 }}
            >
              {/* Sticky gate header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 14,
                position: "sticky",
                top: 0,
                background: "#F9FAFB",
                paddingTop: 8,
                paddingBottom: 8,
                zIndex: 10,
                borderBottom: "1px solid #E5E7EB",
              }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#374151",
                  background: "#F3F4F6",
                  border: "1px solid #E5E7EB",
                  borderRadius: 6,
                  padding: "4px 10px",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}>
                  CP{cp}
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>
                  {GATE_NAMES[cp]}
                </div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 2 }}>
                  {gateReviewed(cp)}/{gateTotal(cp)} reviewed
                </div>
              </div>

              {/* Catalog cards */}
              {cards.map((card) => {
                const entry = state.decisions[card.id];
                const decision = entry?.decision;
                const isEditOpen = openEditCard === card.id || decision === "edit";
                const currentEditText = editTexts[card.id] ?? entry?.editedText ?? card.label;
                const currentNote = noteTexts[card.id] ?? entry?.notes ?? "";

                return (
                  <AuditCardView
                    key={card.id}
                    card={card}
                    decision={decision}
                    editText={currentEditText}
                    noteText={currentNote}
                    isEditOpen={isEditOpen}
                    borderColor={decisionBorderColor(decision)}
                    onKeep={() => setDecision(card.id, "keep")}
                    onReplace={() => setDecision(card.id, "replace")}
                    onEdit={() => setDecision(card.id, "edit")}
                    onSkip={() => setDecision(card.id, "skip")}
                    onUndo={() => clearDecision(card.id)}
                    onEditTextChange={(v) => saveEditText(card.id, v)}
                    onNoteChange={(v) => saveNote(card.id, v)}
                  />
                );
              })}

              {/* User-added question cards */}
              {userQuestions.map((uq) => (
                <UserAddedCard
                  key={uq.id}
                  cp={cp}
                  question={uq}
                  onChange={(field, value) => updateUserQuestion(cp, uq.id, field, value)}
                  onRemove={() => removeUserQuestion(cp, uq.id)}
                />
              ))}

              {/* Add my own question button */}
              <button
                onClick={() => addUserQuestion(cp)}
                style={{
                  marginTop: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "9px 16px",
                  background: "#fff",
                  border: "1px dashed #525258",
                  borderRadius: 8,
                  color: "#3F3F44",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#F5F5F4")}
                onMouseOut={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 400 }}>+</span>
                Add my own question to CP{cp}
              </button>
            </div>
          );
        })}

        {/* Bottom download */}
        <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 28, marginTop: 8, display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <button
            onClick={downloadDecisions}
            style={{
              padding: "12px 32px",
              background: "#111827",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              letterSpacing: "0.01em",
            }}
          >
            Download my decisions
          </button>
          <div style={{ fontSize: 12, color: "#9CA3AF" }}>
            Saves as cp2-10-audit-v2-decisions-[date].json · Includes all catalog decisions and your added questions
          </div>
          <div style={{ marginTop: 16, marginBottom: 32 }} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuditCardView — a single catalog question card
// ---------------------------------------------------------------------------

interface AuditCardViewProps {
  card: AuditCard;
  decision: AuditDecision | undefined;
  editText: string;
  noteText: string;
  isEditOpen: boolean;
  borderColor: string;
  onKeep: () => void;
  onReplace: () => void;
  onEdit: () => void;
  onSkip: () => void;
  onUndo: () => void;
  onEditTextChange: (v: string) => void;
  onNoteChange: (v: string) => void;
}

function AuditCardView({
  card,
  decision,
  editText,
  noteText,
  isEditOpen,
  borderColor,
  onKeep,
  onReplace,
  onEdit,
  onSkip,
  onUndo,
  onEditTextChange,
  onNoteChange,
}: AuditCardViewProps) {
  const recBadge = REC_BADGE[card.recommendation];
  const hasDecision = decision !== undefined;
  const decBadge = decision ? DECISION_BADGE[decision] : null;

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderLeft: `4px solid ${hasDecision ? borderColor : "#E5E7EB"}`,
      borderRadius: 8,
      padding: "18px 20px",
      marginBottom: 10,
      transition: "border-left-color 0.25s",
    }}>
      {/* Eyebrow row */}
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          padding: "2px 7px",
          borderRadius: 4,
          background: recBadge.bg,
          color: recBadge.color,
          textTransform: "uppercase",
        }}>
          {card.recommendation}
        </span>
        {card.type === "NEW" && (
          <span style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            padding: "2px 7px",
            borderRadius: 4,
            background: "#EFEFEE",
            color: "#0B0B0F",
            textTransform: "uppercase",
          }}>
            Proposed new
          </span>
        )}
        {card.hasHardStop && (
          <span style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            padding: "2px 7px",
            borderRadius: 4,
            background: "#FEE2E2",
            color: "#991B1B",
            textTransform: "uppercase",
          }}>
            Has hard stop
          </span>
        )}
        {hasDecision && decBadge && (
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "2px 7px",
            borderRadius: 4,
            background: decBadge.bg,
            color: decBadge.color,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}>
            {decBadge.label}
          </span>
        )}
        {card.questionKey && (
          <span style={{ fontSize: 11, fontFamily: "monospace", color: "#9CA3AF", marginLeft: "auto" }}>
            {card.questionKey}
          </span>
        )}
        {!card.questionKey && (
          <span style={{ fontSize: 11, fontFamily: "monospace", color: "#E5E5E7", marginLeft: "auto" }}>
            NEW
          </span>
        )}
      </div>

      {/* Question text */}
      <div style={{ fontSize: 15, fontWeight: 600, color: "#111827", lineHeight: 1.5, marginBottom: 8 }}>
        {card.label}
      </div>

      {/* Options summary */}
      <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.6, marginBottom: 8 }}>
        <span style={{ color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.09em", fontSize: 10 }}>Options: </span>
        {card.optionsSummary}
      </div>

      {/* Rationale */}
      <div style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.6, marginBottom: 14, padding: "10px 12px", background: "#F9FAFB", borderRadius: 6, border: "1px solid #F3F4F6" }}>
        {card.rationale}
      </div>

      {/* Edit text area */}
      {isEditOpen && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: "#4B5563", display: "block", marginBottom: 5, fontWeight: 500 }}>
            Your edited question text:
          </label>
          <textarea
            value={editText}
            onChange={(e) => onEditTextChange(e.target.value)}
            rows={3}
            style={{
              width: "100%",
              fontSize: 14,
              padding: "10px 12px",
              border: "1px solid #C9C9CC",
              borderRadius: 7,
              fontFamily: "inherit",
              color: "#111827",
              boxSizing: "border-box",
              resize: "vertical",
              background: "#FAFAF7",
              lineHeight: 1.5,
            }}
          />
        </div>
      )}

      {/* Notes field */}
      <div style={{ marginBottom: 14 }}>
        <textarea
          value={noteText}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Notes (optional) — visible in the downloaded JSON"
          rows={1}
          style={{
            width: "100%",
            fontSize: 12,
            padding: "8px 10px",
            border: "1px solid #E5E7EB",
            borderRadius: 6,
            fontFamily: "inherit",
            color: "#374151",
            boxSizing: "border-box",
            resize: "vertical",
            background: "#fff",
          }}
        />
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
        <ActionBtn
          label="Keep"
          active={decision === "keep"}
          activeStyle={{ background: "#16A34A", color: "#fff", borderColor: "#16A34A" }}
          inactiveStyle={{ background: "#fff", color: "#065F46", borderColor: "#6EE7B7" }}
          onClick={onKeep}
        />
        <ActionBtn
          label="Replace"
          active={decision === "replace"}
          activeStyle={{ background: "#EAB308", color: "#fff", borderColor: "#EAB308" }}
          inactiveStyle={{ background: "#fff", color: "#92400E", borderColor: "#FCD34D" }}
          onClick={onReplace}
        />
        <ActionBtn
          label="Edit text"
          active={decision === "edit"}
          activeStyle={{ background: "#525258", color: "#fff", borderColor: "#525258" }}
          inactiveStyle={{ background: "#fff", color: "#0B0B0F", borderColor: "#C9C9CC" }}
          onClick={onEdit}
        />
        <ActionBtn
          label="Skip"
          active={decision === "skip"}
          activeStyle={{ background: "#F3F4F6", color: "#374151", borderColor: "#D1D5DB" }}
          inactiveStyle={{ background: "#fff", color: "#6B7280", borderColor: "#E5E7EB" }}
          onClick={onSkip}
        />
        {hasDecision && (
          <button
            onClick={onUndo}
            style={{ padding: "6px 10px", fontSize: 12, color: "#9CA3AF", background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline", marginLeft: 2 }}
          >
            Undo
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionBtn helper
// ---------------------------------------------------------------------------

interface ActionBtnProps {
  label: string;
  active: boolean;
  activeStyle: React.CSSProperties;
  inactiveStyle: React.CSSProperties;
  onClick: () => void;
}

function ActionBtn({ label, active, activeStyle, inactiveStyle, onClick }: ActionBtnProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 600,
        borderRadius: 6,
        border: "1px solid",
        cursor: "pointer",
        transition: "all 0.15s",
        ...(active ? activeStyle : inactiveStyle),
      }}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// UserAddedCard — Bryan's own question proposal
// ---------------------------------------------------------------------------

interface UserAddedCardProps {
  cp: number;
  question: UserAddedQuestion;
  onChange: (field: keyof UserAddedQuestion, value: string) => void;
  onRemove: () => void;
}

function UserAddedCard({ cp, question, onChange, onRemove }: UserAddedCardProps) {
  return (
    <div style={{
      background: "#FEFCE8",
      border: "1px dashed #FCD34D",
      borderLeft: "4px dashed #EAB308",
      borderRadius: 8,
      padding: "18px 20px",
      marginBottom: 10,
      marginTop: 4,
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          padding: "2px 8px",
          borderRadius: 4,
          background: "#FEF3C7",
          color: "#92400E",
          textTransform: "uppercase",
          border: "1px solid #FCD34D",
        }}>
          User-Added
        </span>
        <span style={{ fontSize: 12, color: "#92400E", fontWeight: 500 }}>
          Your question — CP{cp}
        </span>
        <button
          onClick={onRemove}
          style={{
            marginLeft: "auto",
            padding: "4px 9px",
            fontSize: 12,
            color: "#B45309",
            background: "#FEF3C7",
            border: "1px solid #FCD34D",
            borderRadius: 5,
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          Remove
        </button>
      </div>

      {/* Question prompt */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: "#78350F", fontWeight: 600, display: "block", marginBottom: 5 }}>
          Question prompt
        </label>
        <input
          type="text"
          value={question.prompt}
          onChange={(e) => onChange("prompt", e.target.value)}
          placeholder="Type the question as it would appear on the card..."
          style={{
            width: "100%",
            fontSize: 14,
            padding: "9px 12px",
            border: "1px solid #FCD34D",
            borderRadius: 7,
            fontFamily: "inherit",
            color: "#111827",
            boxSizing: "border-box",
            background: "#fff",
          }}
        />
      </div>

      {/* What it tests / rationale */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: "#78350F", fontWeight: 600, display: "block", marginBottom: 5 }}>
          What this tests / why it qualifies or disqualifies
        </label>
        <textarea
          value={question.rationale}
          onChange={(e) => onChange("rationale", e.target.value)}
          placeholder="Explain what a good answer vs. a bad answer signals about this deal..."
          rows={3}
          style={{
            width: "100%",
            fontSize: 13,
            padding: "9px 12px",
            border: "1px solid #FCD34D",
            borderRadius: 7,
            fontFamily: "inherit",
            color: "#374151",
            boxSizing: "border-box",
            resize: "vertical",
            background: "#fff",
            lineHeight: 1.5,
          }}
        />
      </div>

      {/* Suggested options */}
      <div>
        <label style={{ fontSize: 12, color: "#78350F", fontWeight: 600, display: "block", marginBottom: 5 }}>
          Suggested options (one per line, add flag if applicable: OK / Orange / Review / Disqualify)
        </label>
        <textarea
          value={question.options}
          onChange={(e) => onChange("options", e.target.value)}
          placeholder={`Yes — confirmed (OK)\nPartial — in progress (Orange)\nNo — not addressed (Review)`}
          rows={4}
          style={{
            width: "100%",
            fontSize: 13,
            padding: "9px 12px",
            border: "1px solid #FCD34D",
            borderRadius: 7,
            fontFamily: "inherit",
            color: "#374151",
            boxSizing: "border-box",
            resize: "vertical",
            background: "#fff",
            lineHeight: 1.6,
          }}
        />
      </div>
    </div>
  );
}
